import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Typography } from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';

import MUIModal from '../../../common/MUIModal';
import { sectionCardSx, sectionTitleSx, primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, SUCCESS, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { formatDate } from '../../../helpers/UtilityHelper';
import { planGst, planTotal } from '../data/plans';
import PaymentOtpModal from './PaymentOtpModal';

/**
 * Offers published to this merchant.
 *
 * Sits ABOVE the standard price list, because an offer exists precisely to be taken instead of
 * list price — a merchant who was quoted ₹12,000 should not have to find it under the ₹15,000
 * card. When there are no offers the whole block renders nothing, so the page is unchanged for
 * the overwhelming majority of merchants.
 *
 * Two kinds arrive together and deliberately look the same to the merchant: a PUBLIC promotion
 * and a TARGETED deal negotiated for them. The only visible difference is the "For you" chip,
 * which a targeted offer earns and a public one does not.
 *
 * ACCEPTING BUYS. The button says so and shows the exact amount, because the next thing that
 * happens is a wallet debit.
 */

const bodySx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.55 };
const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.45 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const offerCardSx = {
  border: `1px solid ${PRIMARY_BLUE}55`,
  borderRadius: RADIUS.md,
  background: LIGHT_BG,
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: GAP.sm,
};

/** Unlimited is a real value on an offer, not missing data. */
const shipmentsLabel = (n) =>
  n === null || n === undefined ? 'Unlimited' : Number(n).toLocaleString('en-IN');

export default function OffersInbox({ offers, loading, error, planActive, onMarkViewed, onRespond }) {
  const [declining, setDeclining] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [localError, setLocalError] = useState(null);
  /* Accepting buys, so it goes through the email code like any other payment. */
  const [accepting, setAccepting] = useState(null);

  /*
    Stamp first view once, for whatever is on screen. Sales uses this to tell "ignored" from
    "declined" — an offer that lapsed unseen is a different conversation from one that was read
    and turned down.
  */
  useEffect(() => {
    offers.forEach((o) => {
      if (o.myStatus === 'PENDING') onMarkViewed(o.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers.map((o) => o.id).join(',')]);

  // Nothing to show and nothing to explain — stay out of the page entirely.
  if (!loading && !error && offers.length === 0) return null;


  const decline = async (reason) => {
    const offer = declining;
    setBusyId(offer.id);
    setLocalError(null);
    try {
      await onRespond(offer.id, false, reason);
      setDeclining(null);
    } catch (e) {
      setLocalError(e?.message || 'Could not decline this offer.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={sectionCardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, mb: 1 }}>
        <LocalOfferOutlinedIcon sx={{ fontSize: 18, color: PRIMARY_BLUE }} />
        <Typography sx={sectionTitleSx}>Offers for you</Typography>
      </Box>

      {loading && <CircularProgress size={18} />}
      {error && <Alert severity='warning' sx={{ mb: 1 }}>{error}</Alert>}
      {localError && <Alert severity='error' sx={{ mb: 1 }}>{localError}</Alert>}

      {planActive && offers.length > 0 && (
        <Alert severity='info' sx={{ mb: 1 }}>
          You are already on a plan. To move to one of these, speak to your ProShip account
          manager — a mid-period change is not something you can do yourself.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.md }}>
        {offers.map((o) => {
          const total = planTotal(o);
          const gst = planGst(o);
          const busy = busyId === o.id;
          return (
            <Box key={o.id} sx={offerCardSx}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: GAP.sm, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY }}>
                  {o.name || `${o.planCode} plan`}
                </Typography>
                {o.myStatus && (
                  <Chip size='small' label='For you'
                        sx={{ height: 19, fontSize: SIZE['4xs'], bgcolor: `${SUCCESS}14`, color: SUCCESS }} />
                )}
                {o.offerNo && <Typography sx={smallSx}>{o.offerNo}</Typography>}
              </Box>

              {o.notes && <Typography sx={bodySx}>{o.notes}</Typography>}

              <Divider />

              <Box sx={{ display: 'flex', gap: GAP.lg, flexWrap: 'wrap' }}>
                <Stat label='Shipments included' value={shipmentsLabel(o.shipmentsIncluded)} />
                <Stat label='Over-usage' value={o.overagePrice ? `${inr(o.overagePrice)}/shpt` : 'Free'} />
                <Stat label='Courier contracts' value={shipmentsLabel(o.courierIntegrationLimit)} />
                <Stat label='Period' value={o.periodDays ? `${o.periodDays} days` : 'No time bound'} />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: GAP.md, flexWrap: 'wrap' }}>
                <Box>
                  <Typography sx={smallSx}>
                    {inr(o.planFee)} + {inr(gst)} GST
                    {o.offerValidTill ? ` · offer valid till ${formatDate(o.offerValidTill, 'dd MMM yyyy')}` : ''}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: SIZE.lg, fontWeight: WEIGHT.bold, color: NAVY }}>
                    {inr(total)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: GAP.sm }}>
                  <Button size='small' sx={secondaryBtnSx} disabled={busy}
                          onClick={() => { setLocalError(null); setDeclining(o); }}>
                    Decline
                  </Button>
                  <Button
                    size='small'
                    variant='contained'
                    sx={primaryBtnSx}
                    disabled={busy || planActive}
                    startIcon={busy ? <CircularProgress size={12} /> : null}
                    onClick={() => { setLocalError(null); setAccepting(o); }}
                  >
                    {busy ? 'Charging…' : total > 0 ? `Accept and pay ${inr(total)}` : 'Accept'}
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {accepting && (
        <PaymentOtpModal
          title={`Accept ${accepting.name || accepting.planCode}`}
          payLabel={planTotal(accepting) > 0 ? `Verify and pay ${inr(planTotal(accepting))}` : 'Verify and start'}
          summary={
            <Typography sx={bodySx}>
              {planTotal(accepting) > 0
                ? `${inr(planTotal(accepting))} (including GST) will be charged now, and the plan starts today.`
                : 'Nothing is charged. The plan starts today and ends with its period; it does not renew.'}
            </Typography>
          }
          onPay={(proof) => onRespond(accepting.id, true, null, proof)}
          onClose={() => setAccepting(null)}
        />
      )}

      {declining && (
        <MUIModal
          open
          maxWidth='sm'
          title={`Decline ${declining.name || declining.planCode}?`}
          handleClose={() => setDeclining(null)}
        >
          <DeclineBody onCancel={() => setDeclining(null)} onConfirm={decline} busy={!!busyId} />
        </MUIModal>
      )}
    </Box>
  );
}

const Stat = ({ label, value }) => (
  <Box>
    <Typography sx={smallSx}>{label}</Typography>
    <Typography sx={strongSx}>{value}</Typography>
  </Box>
);

/**
 * A reason is optional, not required. A merchant should be able to say no without justifying
 * it — but sales genuinely uses the answer, so it is asked for.
 */
function DeclineBody({ onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState('');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.md }}>
      <Typography sx={bodySx}>
        The offer disappears from your panel. Nothing is charged, and you can still buy a standard
        plan at list price.
      </Typography>
      <Box
        component='textarea'
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder='Anything you would like us to know? (optional)'
        sx={{
          fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, p: 1.25,
          border: `1px solid ${BORDER}`, borderRadius: RADIUS.sm, resize: 'vertical',
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: GAP.sm }}>
        <Button size='small' sx={secondaryBtnSx} onClick={onCancel} disabled={busy}>Keep it</Button>
        <Button size='small' variant='contained' sx={primaryBtnSx} disabled={busy}
                onClick={() => onConfirm(reason)}>
          {busy ? 'Declining…' : 'Decline offer'}
        </Button>
      </Box>
    </Box>
  );
}
